import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

/**
 * Window event dispatched when a data request is rejected because the session
 * token is missing/expired/invalid. WebAuthnContext listens for this and runs
 * the normal logout + "session expired" message. Kept as a window event so this
 * module stays free of React imports (same pattern as CACHE_UPDATED_EVENT).
 */
export const AUTH_EXPIRED_EVENT = 'bookmark-auth-expired';

/**
 * Idempotency guard: many data requests can be in flight at once, and each could
 * come back 401 after the token expires. Without this, every one of them would
 * fire AUTH_EXPIRED_EVENT and we'd trigger N logouts. We fire exactly one, then
 * ignore further auth failures until the next successful login resets the flag.
 */
let authExpiredHandled = false;

function handleAuthExpired() {
  if (authExpiredHandled) return;
  authExpiredHandled = true;
  try {
    localStorage.removeItem('session_token');
  } catch {
    // ignore storage errors
  }
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
}

/**
 * Call after a successful login/registration/session-restore so a future token
 * expiry can be detected and handled again. Without this reset, the one-shot
 * guard above would permanently suppress expiry handling after the first logout.
 */
export function resetAuthExpiredGuard() {
  authExpiredHandled = false;
}

/**
 * The single module that owns how the app authenticates to Supabase, in BOTH
 * directions (Phase 9 JWT bridge):
 *
 *  - (in)  Every request reads the current `session_token` from localStorage at
 *          call time and sends it as `Authorization: Bearer <token>`. Reading at
 *          call time (not client-construction time) means login/logout/restore
 *          are reflected immediately without recreating the client. With a valid
 *          bridged token (role: "authenticated"), PostgREST runs the request as
 *          the `authenticated` role. The `apikey` header stays the anon key —
 *          Supabase requires an API key, but the ROLE comes from the bearer JWT.
 *
 *  - (out) If a request is rejected for auth reasons (401, or a 403/400 whose
 *          body indicates a JWT problem), we centrally clear the token and signal
 *          AUTH_EXPIRED_EVENT so the app logs out gracefully instead of showing
 *          silent empty data. Every read and write flows through this one fetch,
 *          so this is the single chokepoint — no per-hook handling needed.
 *
 * persistSession/autoRefreshToken are off: we mint and manage the token
 * ourselves (via the WebAuthn lambdas), so GoTrue must not try to manage a
 * session it did not create.
 */
export const supabaseData = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
  },
  global: {
    fetch: async (input, init) => {
      let token: string | null = null;
      try {
        token = localStorage.getItem('session_token');
      } catch {
        token = null;
      }

      const headers = new Headers(init?.headers);
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      const response = await fetch(input, { ...init, headers });

      // Only treat it as an auth expiry if we actually sent a token and the
      // server rejected it. A 401 with no token present is just an
      // unauthenticated call (e.g. pre-login) and must not force a logout loop.
      if (token && isAuthRejection(response.status)) {
        handleAuthExpired();
      }

      return response;
    },
  },
});

/**
 * PostgREST returns 401 for a missing/invalid/expired JWT. We treat 401 as the
 * auth-expiry signal. (403 is used for RLS "insufficient privilege" on a VALID
 * token, so we deliberately do NOT log out on 403 — that's a policy denial, not
 * an expired session.)
 */
function isAuthRejection(status: number): boolean {
  return status === 401;
}

/**
 * Authenticate the Realtime WebSocket with the current session JWT so that,
 * under RLS, the socket's role is `authenticated` and postgres_changes events
 * are actually delivered. REST already injects this token per-request via the
 * global.fetch wrapper above; Realtime must be set separately because supabase-js
 * only upgrades the socket via GoTrue auth events, which this app disables
 * (persistSession/autoRefreshToken off). Pass null (or omit on logout) to fall
 * back to the anon key, dropping the socket's role back to `anon`.
 */
export function setRealtimeAuth(token: string | null): void {
  try {
    // Authenticated socket when a token is present; anon key otherwise.
    supabaseData.realtime.setAuth(token ?? supabaseAnonKey);
  } catch {
    // Best-effort; never throw from auth wiring.
  }
}

/** Read session_token from localStorage and apply it to the Realtime socket. */
export function syncRealtimeAuthFromStorage(): void {
  let token: string | null = null;
  try {
    token = localStorage.getItem('session_token');
  } catch {
    token = null;
  }
  setRealtimeAuth(token);
}
